import type { SettingsResponse, SettingsSending } from "../../shared/types";
import { usePhone } from "../components/phone/usePhone";
import { api } from "../lib/api";
import {
  type BannerTone,
  ratePct,
  SENDING_STATUS_META,
  type SendingStateId,
  SPAM_PAUSE_LINE,
} from "./sendingProtection";

type Run = (fn: () => Promise<SettingsResponse>, ok: string) => Promise<boolean>;

/** Status-pill tone → the Settings pill class (brass = amber warn, bad = red, ink = dark). */
const PILL_CLASS: Record<BannerTone, string> = { brass: "warn", bad: "bad", ink: "ink" };

function StatusPill({ tone, label }: { tone: BannerTone; label: string }) {
  return (
    <span className={`pill ${PILL_CLASS[tone]}`}>
      <span className="dot" />
      {label}
    </span>
  );
}

/**
 * The in-app sending status / recovery pages (B3), shown inside Settings →
 * Sending. Which page renders is driven by the escalation state the backend sets:
 * heads-up (getting close), paused / final (resume), recovering (watched-trial
 * progress), stopped. Resume calls `POST /settings/sending/resume` and then
 * refreshes the app-wide banner. Copy is ported from the locked prototype.
 */
export function SendingStatusPanel({
  s,
  run,
  refreshGlobal,
}: {
  s: SettingsSending;
  run: Run;
  refreshGlobal: () => void;
}) {
  const state = s.state as SendingStateId;
  const meta = SENDING_STATUS_META[state as Exclude<SendingStateId, "healthy">];
  if (!meta) return null;

  const spam = ratePct(s.spam_rate);
  const line = ratePct(SPAM_PAUSE_LINE);

  const resume = async () => {
    const okDone = await run(() => api.settings.resumeSending(), "Sending resumed. Recovery has started");
    if (okDone) refreshGlobal();
  };

  return (
    <section className="send-status">
      <StatusPill tone={meta.pill} label={meta.pillLabel} />
      <h2>{meta.heading}</h2>

      {state === "headsup" ? (
        <p className="send-lead">
          Spam complaints are at <b>{spam}%</b>, and the pause line is {line}%. Nothing’s paused yet.
          Emailing only people who want to hear from you keeps you clear.
        </p>
      ) : null}

      {state === "paused" ? (
        <p className="send-lead">
          Too many people marked your recent emails as spam: <b>{spam}%</b>, and our limit is {line}%.
          We’ve paused it to protect inbox delivery. Sending only to people who expect you is yours to
          manage.
        </p>
      ) : null}

      {state === "final" ? (
        <p className="send-lead">
          This is your second pause. <b>One more spam problem and your sending is turned off.</b> Resume
          and keep it clean.
        </p>
      ) : null}

      {state === "recovering" ? (
        <p className="send-lead">
          On a reduced daily limit while we check your next emails. Stay under the line and full sending
          returns automatically.
        </p>
      ) : null}

      {state === "stopped" ? (
        <p className="send-lead">
          After repeated spam problems, we’ve stopped your sending to protect everyone.
        </p>
      ) : null}

      {state === "paused" || state === "final" ? (
        <>
          <div className="card send-card">
            <h3>Ready to try again?</h3>
            <p>
              When you resume, your next emails go out on a reduced daily limit while we watch them. Stay
              under the line and full sending returns on its own.
            </p>
            <button type="button" className="btn" onClick={resume}>
              Resume sending
            </button>
          </div>
          <TellUs lead="Think this is a mistake? Tell us:" />
        </>
      ) : null}

      {state === "recovering" ? <RecoveryProgress s={s} /> : null}

      {state === "stopped" ? <TellUs lead="Think this is a mistake? Tell us and a person will look:" /> : null}
    </section>
  );
}

/** Where to reach a person: Show & tell in the Flashsuite menu, on a phone at the top of More. */
function TellUs({ lead }: { lead: string }) {
  const phone = usePhone();
  return (
    <p className="send-links">
      {lead}{" "}
      {phone
        ? "open More, tap your business at the top, then Show & tell."
        : "click your business at the top left (its mark, when the side bar is folded), then Show & tell."}
    </p>
  );
}

/** The watched-trial card: your next emails are watched; keep the rates clean. */
function RecoveryProgress({ s }: { s: SettingsSending }) {
  return (
    <>
      <div className="card send-card prog">
        <div className="prog-sub">
          Your next emails are on a watched trial. Keep spam and bounces under the line and full
          sending returns automatically.
        </div>
        <div className="rstats">
          <div className="rstat">
            <div className="l">New spam</div>
            <div className="v" style={{ color: "var(--good)" }}>
              {ratePct(s.spam_rate)}%
            </div>
          </div>
          <div className="rstat">
            <div className="l">New bounces</div>
            <div className="v" style={{ color: "var(--good)" }}>
              {ratePct(s.bounce_rate)}%
            </div>
          </div>
        </div>
      </div>
      <TellUs lead="Questions? Ask us:" />
    </>
  );
}
